import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { cn } from "../../components/shared/Card";
import { CollapsibleComponent } from "../../components/shared/Collapsible";
import { Input } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import Loader from "../../shared/Loader";
import { formatDisplayDate } from "../../utils/date";
import {
  getLotExerciseExtensionDetails,
  extendExerciseLots,
} from "../../api/Esop";

interface VestingLot {
  lotId: string;
  vestingDate: string;
  vested: number;
  exercised: number;
  netVested: number;
  currentExpiryDate: string | null;
  newExpiryDate: string;
  originalExpiryDate?: string;
}

interface GrantLotData {
  grantId: string;
  identifier: string;
  planName: string;
  dateOfGrant: string;
  employeeName: string;
  lots: VestingLot[];
}

type BulkMode = "duration" | "date";
type DurUnit = "days" | "months" | "years";

// ── Helpers ──────────────────────────────────────────────────────────────────

function toDateInput(val: string | null | undefined): string {
  if (!val) return "";
  // Normalise ISO datetime → YYYY-MM-DD
  return val.split("T")[0];
}

function addDuration(base: string, value: number, unit: DurUnit): string {
  const d = new Date(base);
  if (unit === "days") d.setDate(d.getDate() + value);
  else if (unit === "months") d.setMonth(d.getMonth() + value);
  else d.setFullYear(d.getFullYear() + value);
  return d.toISOString().split("T")[0];
}

function applyBulkToLots(
  lots: VestingLot[],
  mode: "date" | "duration",
  opts: { date?: string; value?: number; unit?: DurUnit }
): VestingLot[] {
  return lots.map((lot) => {
    if (mode === "date")
      return { ...lot, newExpiryDate: opts.date ?? lot.newExpiryDate };
    // duration — base off current expiry, fallback to vesting date
    const base =
      toDateInput(lot.currentExpiryDate) || toDateInput(lot.vestingDate);
    if (!base) return lot;
    return {
      ...lot,
      newExpiryDate: addDuration(base, opts.value!, opts.unit!),
    };
  });
}

function LotTable({
  lots,
  onChange,
}: {
  lots: VestingLot[];
  onChange: (updated: VestingLot[]) => void;
}) {
  const updateLot = (idx: number, patch: Partial<VestingLot>) => {
    const next = lots.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    onChange(next);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500 uppercase">
            <th className="py-2 px-3 text-left font-medium">Vesting Date</th>
            <th className="py-2 px-3 text-right font-medium">Vested</th>
            <th className="py-2 px-3 text-right font-medium">Exercised</th>
            <th className="py-2 px-3 text-right font-medium">Net Vested</th>
            <th className="py-2 px-3 text-left font-medium">Current Expiry</th>
            <th className="py-2 px-3 text-left font-medium">New Expiry</th>
          </tr>
        </thead>
        <tbody>
          {lots.map((lot, idx) => {
            const changed = lot.newExpiryDate !== lot.originalExpiryDate;
            return (
              <tr
                key={lot.lotId}
                className={cn(
                  "border-b border-gray-100 last:border-0",
                  changed ? "" : "bg-white"
                )}
              >
                <td className="py-2 px-3 text-gray-700">
                  {formatDisplayDate(lot.vestingDate)}
                </td>
                <td className="py-2 px-3 text-right text-gray-700">
                  {lot.vested?.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-right text-gray-700">
                  {lot.exercised?.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-right font-medium text-gray-900">
                  {lot.netVested?.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-gray-500">
                  {lot.currentExpiryDate &&
                  new Date(lot.currentExpiryDate).toString() !==
                    "Invalid Date" ? (
                    formatDisplayDate(lot.currentExpiryDate)
                  ) : (
                    <span className="text-gray-400">Never Expires</span>
                  )}
                </td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1">
                    <Input
                      type="date"
                      className="w-48 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400"
                      value={lot.newExpiryDate}
                      onChange={(e) =>
                        updateLot(idx, { newExpiryDate: e.target.value })
                      }
                    />
                    {changed && (
                      <button
                        type="button"
                        title="Revert to original"
                        className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors"
                        onClick={() =>
                          updateLot(idx, {
                            newExpiryDate: lot.originalExpiryDate ?? "",
                          })
                        }
                      >
                        <Icon icon="mdi:close" className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GrantCard({
  grant,
  isExpanded,
  onToggle,
  onChange,
}: {
  grant: GrantLotData;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (updated: GrantLotData) => void;
}) {
  const totalNet = grant.lots.reduce((s, l) => s + (l.netVested || 0), 0);
  const changedCount = grant.lots.filter(
    (l) => l.newExpiryDate !== l.originalExpiryDate
  ).length;

  const label = (
    <HStack className="items-center gap-2 flex-wrap">
      <span className="font-medium text-sm text-gray-900">
        {grant.identifier}
      </span>
      <span className="text-gray-400 text-xs">·</span>
      <span className="text-xs text-gray-600">{grant.planName}</span>
      <span className="text-gray-400 text-xs">·</span>
      <span className="text-xs text-gray-500">
        {formatDisplayDate(grant.dateOfGrant)}
      </span>
      {grant.employeeName && (
        <>
          <span className="text-gray-400 text-xs">·</span>
          <span className="text-xs text-gray-500">{grant.employeeName}</span>
        </>
      )}
    </HStack>
  );

  const labelDetail = (
    <HStack className="gap-4 text-xs text-gray-500">
      <span>{totalNet.toLocaleString()} net vested</span>
      <span>
        {grant.lots.length} lot{grant.lots.length !== 1 ? "s" : ""}
      </span>
      {changedCount > 0 && (
        <span className="text-orange-601 font-medium">
          {changedCount} updated
        </span>
      )}
    </HStack>
  );

  return (
    <CollapsibleComponent
      label={label}
      labelDetail={labelDetail}
      isOpen={!isExpanded}
      onClick={onToggle}
    >
      <LotTable
        lots={grant.lots}
        onChange={(updatedLots) => onChange({ ...grant, lots: updatedLots })}
      />
    </CollapsibleComponent>
  );
}

// ── Main Dialog ───────────────────────────────────────────────────────────────

interface ExtendExerciseDialogProps {
  open: boolean;
  onClose: () => void;
  grantIds: string[];
  employeeInfo?: { name: string; employeeId?: string; department?: string };
  onSuccess?: () => void;
}

export default function ExtendExerciseDialog({
  open,
  onClose,
  grantIds,
  employeeInfo,
  onSuccess,
}: ExtendExerciseDialogProps) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [grants, setGrants] = useState<GrantLotData[]>([]);
  const [expandedGrants, setExpandedGrants] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState<BulkMode>("duration");
  const [customValue, setCustomValue] = useState<number | "">("");
  const [customUnit, setCustomUnit] = useState<DurUnit>("months");
  const [specificDate, setSpecificDate] = useState("");
  const [bulkBarOpen, setBulkBarOpen] = useState(true);

  // ── Data fetching ──────────────────────────────────────────────────────────
  // stringify to get stable reference without adding grantIds to deps
  const stableGrantIds = useMemo(() => grantIds, [JSON.stringify(grantIds)]); // eslint-disable-line

  useEffect(() => {
    if (!open || stableGrantIds.length === 0) return;
    setLoading(true);
    setFetchError(null);
    setGrants([]);
    setExpandedGrants(new Set());

    getLotExerciseExtensionDetails(stableGrantIds)
      .then((data: GrantLotData[]) => {
        const withOriginals = data.map((g) => ({
          ...g,
          lots: g.lots.map((l) => {
            const expiry = toDateInput(l.currentExpiryDate);
            return {
              ...l,
              vestingDate: toDateInput(l.vestingDate),
              currentExpiryDate: expiry || null,
              newExpiryDate: expiry,
              originalExpiryDate: expiry,
            };
          }),
        }));
        setGrants(withOriginals);
        // expand first grant by default
        if (withOriginals.length > 0) {
          setExpandedGrants(new Set([withOriginals[0].grantId]));
        }
      })
      .catch((err: any) => {
        setFetchError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch details"
        );
      })
      .finally(() => setLoading(false));
  }, [open, stableGrantIds]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const totalNetVested = grants.reduce(
    (s, g) => s + g.lots.reduce((ls, l) => ls + l.netVested, 0),
    0
  );
  const totalChanged = grants.reduce(
    (s, g) =>
      s + g.lots.filter((l) => l.newExpiryDate !== l.originalExpiryDate).length,
    0
  );

  // ── Actions ────────────────────────────────────────────────────────────────
  const applyBulk = (
    mode: "date" | "duration",
    opts: { date?: string; value?: number; unit?: DurUnit }
  ) => {
    setGrants((prev) =>
      prev.map((g) => ({ ...g, lots: applyBulkToLots(g.lots, mode, opts) }))
    );
  };

  const toggleGrant = (id: string) => {
    setExpandedGrants((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateGrant = (updated: GrantLotData) => {
    setGrants((prev) =>
      prev.map((g) => (g.grantId === updated.grantId ? updated : g))
    );
  };

  const handleSave = async () => {
    const updates = grants.flatMap((g) =>
      g.lots
        .filter((l) => l.newExpiryDate !== l.originalExpiryDate)
        .map((l) => ({ lotId: l.lotId, newExpiryDate: l.newExpiryDate }))
    );
    if (updates.length === 0) return;

    setSaving(true);
    try {
      await extendExerciseLots(updates);
      toast.success("Exercise period extended successfully");
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const allIds = grants.map((g) => g.grantId);
  const allExpanded =
    allIds.length > 0 && allIds.every((id) => expandedGrants.has(id));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Box className="bg-white flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-10 text-lg font-medium border-b py-7 shrink-0">
          <h6 className="flex justify-between items-start">
            <div>
              Extend Exercise Period
              {employeeInfo && (
                <p className="text-sm font-normal text-gray-500 mt-1">
                  {employeeInfo.name}
                  {employeeInfo.employeeId && (
                    <span className="ml-2 text-gray-400">
                      · {employeeInfo.employeeId}
                    </span>
                  )}
                  {employeeInfo.department && (
                    <span className="ml-2 text-gray-400">
                      · {employeeInfo.department}
                    </span>
                  )}
                </p>
              )}
            </div>
            <span
              onClick={onClose}
              className="cursor-pointer text-gray-400 hover:text-gray-600 text-base font-normal"
            >
              X
            </span>
          </h6>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader />
          </div>
        ) : fetchError ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 py-20 text-center px-8">
            <Icon
              icon="mdi:alert-circle-outline"
              className="w-12 h-12 text-red-400"
            />
            <p className="text-gray-700 font-medium">{fetchError}</p>
            <ButtonPrimary1
              type="button"
              onClick={() => {
                setFetchError(null);
                setLoading(true);
                getLotExerciseExtensionDetails(stableGrantIds)
                  .then((data: GrantLotData[]) => {
                    const withOriginals = data.map((g) => ({
                      ...g,
                      lots: g.lots.map((l) => {
                        const expiry = toDateInput(l.currentExpiryDate);
                        return {
                          ...l,
                          vestingDate: toDateInput(l.vestingDate),
                          currentExpiryDate: expiry || null,
                          newExpiryDate: expiry,
                          originalExpiryDate: expiry,
                        };
                      }),
                    }));
                    setGrants(withOriginals);
                    if (withOriginals.length > 0) {
                      setExpandedGrants(new Set([withOriginals[0].grantId]));
                    }
                  })
                  .catch((err: any) =>
                    setFetchError(
                      err?.response?.data?.message || "Failed to fetch details"
                    )
                  )
                  .finally(() => setLoading(false));
              }}
            >
              Retry
            </ButtonPrimary1>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <VStack className="px-10 py-7 gap-5">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">
                    Grants
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {grants.length}
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">
                    Total Net Vested
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalNetVested.toLocaleString()}
                  </p>
                </div>
                <div
                  className={cn(
                    "border rounded-lg p-4",
                    totalChanged > 0
                      ? "bg-orange-50 border-orange-200"
                      : "bg-white border-gray-200"
                  )}
                >
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">
                    Lots Updated
                  </p>
                  <p
                    className={cn(
                      "text-2xl font-bold",
                      totalChanged > 0 ? "text-orange-601" : "text-gray-900"
                    )}
                  >
                    {totalChanged}
                  </p>
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-2 bg-[#FFF3C7] border border-amber-200 rounded-md px-4 py-3 text-sm text-[#BD7D00]">
                <Icon icon="fe:warning" className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Vested options remaining after the new expiry date will be
                  forfeited.
                </span>
              </div>

              {/* Bulk action bar */}
              <CollapsibleComponent
                label="Apply to All Lots"
                labelDetail=""
                isOpen={!bulkBarOpen}
                onClick={() => setBulkBarOpen((v) => !v)}
              >
                {/* Mode toggle */}
                <HStack className="gap-6 mb-4">
                  {(["duration", "date"] as BulkMode[]).map((m) => (
                    <label
                      key={m}
                      className="flex items-center gap-2 cursor-pointer text-sm text-gray-700"
                    >
                      <input
                        type="radio"
                        className="accent-orange-501"
                        checked={bulkMode === m}
                        onChange={() => setBulkMode(m)}
                      />
                      {m === "duration" ? "Add duration" : "Set specific date"}
                    </label>
                  ))}
                </HStack>

                {bulkMode === "duration" ? (
                  <VStack className="gap-4 pt-2">
                    {/* Quick chips */}
                    <HStack className="gap-2 flex-wrap">
                      {[
                        { label: "+1 month", v: 1, u: "months" as DurUnit },
                        { label: "+3 months", v: 3, u: "months" as DurUnit },
                        { label: "+6 months", v: 6, u: "months" as DurUnit },
                        { label: "+1 year", v: 1, u: "years" as DurUnit },
                      ].map((chip) => (
                        <button
                          key={chip.label}
                          type="button"
                          className="px-3 py-1.5 border border-orange-300 bg-white text-orange-700 rounded-full text-xs font-medium hover:bg-orange-100 transition-colors active:scale-95"
                          onClick={() =>
                            applyBulk("duration", {
                              value: chip.v,
                              unit: chip.u,
                            })
                          }
                        >
                          {chip.label}
                        </button>
                      ))}
                    </HStack>
                    <div className="w-full border-t border-gray-200" />
                    {/* Custom input */}
                    <HStack className="items-center gap-2">
                      <Input
                        type="number"
                        className="w-28 border-gray-300"
                        placeholder="Value"
                        value={customValue}
                        onChange={(e) =>
                          setCustomValue(
                            e.target.value ? parseInt(e.target.value, 10) : ""
                          )
                        }
                      />
                      <Select
                        className="w-28 border-gray-300"
                        options={["days", "months", "years"] as DurUnit[]}
                        value={customUnit}
                        onChange={(e) =>
                          setCustomUnit(e.target.value as DurUnit)
                        }
                        textGetter={(o) =>
                          o.charAt(0).toUpperCase() + o.slice(1)
                        }
                        isPlaceHolderDisabled
                      />
                      <ButtonPrimary1
                        type="button"
                        disabled={!customValue}
                        onClick={() => {
                          if (typeof customValue === "number") {
                            applyBulk("duration", {
                              value: customValue,
                              unit: customUnit,
                            });
                          }
                        }}
                      >
                        Apply
                      </ButtonPrimary1>
                    </HStack>
                  </VStack>
                ) : (
                  <HStack className="items-center gap-3">
                    <Input
                      type="date"
                      className="w-48 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                      value={specificDate}
                      onChange={(e) => setSpecificDate(e.target.value)}
                    />
                    <ButtonPrimary1
                      type="button"
                      disabled={!specificDate}
                      onClick={() => applyBulk("date", { date: specificDate })}
                    >
                      Apply to all
                    </ButtonPrimary1>
                  </HStack>
                )}
              </CollapsibleComponent>

              {/* Expand / collapse all */}
              {grants.length > 1 && (
                <HStack className="gap-3 text-sm">
                  <button
                    type="button"
                    className="text-orange-600 hover:underline"
                    onClick={() => setExpandedGrants(new Set(allIds))}
                  >
                    Expand all
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    className="text-orange-600 hover:underline"
                    onClick={() => setExpandedGrants(new Set())}
                  >
                    Collapse all
                  </button>
                </HStack>
              )}

              {/* Grant cards */}
              <VStack className="gap-3">
                {grants.map((g) => (
                  <GrantCard
                    key={g.grantId}
                    grant={g}
                    isExpanded={expandedGrants.has(g.grantId)}
                    onToggle={() => toggleGrant(g.grantId)}
                    onChange={updateGrant}
                  />
                ))}
              </VStack>
            </VStack>
          </div>
        )}

        {/* Footer */}
        {!loading && !fetchError && (
          <div className="shrink-0 px-10 py-5 border-t border-gray-200 bg-white">
            <HStack className="justify-between items-center">
              <ButtonPrimary1 type="button" onClick={onClose}>
                Cancel
              </ButtonPrimary1>
              <HStack className="items-center gap-4">
                {totalChanged > 0 && (
                  <span className="text-sm text-gray-500 italic">
                    {totalChanged} lot{totalChanged !== 1 ? "s" : ""} will be
                    updated
                  </span>
                )}
                <ButtonPrimary
                  type="button"
                  disabled={totalChanged === 0 || saving}
                  onClick={handleSave}
                >
                  {saving ? "Saving…" : "Save Changes"}
                </ButtonPrimary>
              </HStack>
            </HStack>
          </div>
        )}
      </Box>
    </Dialog>
  );
}
